import { createBrowserRouter, RouterProvider } from "react-router-dom";

import theme from "./theme";
import { ThemeProvider } from "@mui/material/styles";

import RootLayout from "./scenes/global/RootLayout";
import Homepage from "./scenes/pages/homepage/Homepage";
import AboutUs from "./scenes/pages/about-us/AboutUs";
import ErrorPage from "./scenes/global/error/ErrorPage";
import Support, { supportDataAction } from "./scenes/pages/support/Support";
import Faq from "./scenes/pages/faq/Faq";
import TermsOfServices from "./scenes/pages/terms/TermsOfServices";
import PrivacyPolicy from "./scenes/pages/terms/PrivacyPolicy";
import Login, { loginAction } from "./scenes/pages/login-register/Login";
import ResetPassword, {
  resetPasswordAction,
} from "./scenes/pages/login-register/ResetPassword";
import CompanyRegister, {
  companyRegisterAction,
} from "./scenes/pages/login-register/CompanyRegister";
import ProffesionalsRegister, {
  proffesionalRegisterAction,
} from "./scenes/pages/login-register/ProffesionalsRegister";
import ForgotPassword, {
  forgotPasswordAction,
} from "./scenes/pages/login-register/ForgotPassword";
import SelectRole from "./scenes/select-role/SelectRole";
import CompanyApproval, {
  companyApprovalAction,
} from "./scenes/select-role/company-approval/CompanyApproval";
import SalesEngineer, {
  salesEngineerAction,
} from "./scenes/select-role/sales-engineer/SalesEngineer";
import CompanyUnderReview from "./scenes/select-role/CompanyUnderReview";
import ProffesionalProfile from "./scenes/my-profile/employee-profile/ProffesionalProfile";
import EditProfilePage, {
  editProfileAction,
} from "./scenes/my-profile/employee-profile/edit-profile/EditProfilePage";
import GoogleCallback from "./components/GoogleCallback";
import CompanyEditProfile, {
  editCompanyProfileAction,
} from "./scenes/my-profile/company-profile/company-profile-edit/CompanyEditProfile";
import MyJobsPage from "./scenes/my-jobs/MyJobsPage";
import CandidatesPage from "./scenes/my-jobs/candidates/CandidatesPage";
import Security, { securityActionData } from "./scenes/pages/security/Security";
import CreateNewJob from "./scenes/my-jobs/CreateNewJob";
import ProffesionalsJobsAll from "./scenes/proffesionals-jobs/ProffesionalsJobsAll";
import ProffesionalsMyJobs from "./scenes/proffesionals-jobs/ProffesionalsMyJobs";

import DashRootLayout from "./scenes/dashboard/DashRootLayout";
import AdminLogin, { adminAction } from "./scenes/dashboard/login/AdminLogin";
import ForgotPasswordAdmin from "./scenes/dashboard/login/ForgotPasswordAdmin";
import ResetPasswordAdmin, {
  resetPasswordAdminAction,
} from "./scenes/dashboard/login/ResetPasswordAdmin";
import Dashboard from "./scenes/dashboard/pages/Dashboard";
import DashboardJobs from "./scenes/dashboard/pages/jobs/DashboardJobs";
import AdminsPage from "./scenes/dashboard/pages/admins/AdminsPage";
import SelectCandidates from "./scenes/dashboard/pages/jobs/SelectCanditates";
import ProfesionalsPage from "./scenes/dashboard/pages/proffesionals/ProfesionalsPage";
import CompaniesPage from "./scenes/dashboard/pages/companies/CompaniesPage";

import Chat from "./scenes/chat/Chat";

import { logoutAction, logoutActionAdmin } from "./auth/Logout";
import {
  tokenLoader,
  checkAuthForLogin,
  checkAuthToken,
  checkEmployeeAccess,
  checkCompanyAccess,
  checkAuthForProfessionals,
  checkAuthForCompany,
  checkCompanyReview,
  checkLoggedInUsers,
  checkAdminAuth,
  checkAdminIsLogged,
} from "./auth/auth";
import JobFormDash, {
  createJobAction,
} from "./scenes/dashboard/pages/jobs/JobFormDash";

const routes = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    id: "root",
    loader: tokenLoader,
    children: [
      {
        index: true,
        element: <Homepage />,
      },
      {
        path: "about-us",
        element: <AboutUs />,
      },
      {
        path: "support",
        element: <Support />,
        action: supportDataAction,
      },
      {
        path: "faq",
        element: <Faq />,
      },
      {
        path: "terms-of-services",
        element: <TermsOfServices />,
      },
      {
        path: "privacy-policy",
        element: <PrivacyPolicy />,
      },
      {
        path: "security",
        element: <Security />,
        loader: checkAuthToken,
        action: securityActionData,
      },
      {
        loader: checkAuthForProfessionals,
        children: [
          {
            path: "/my-profile/:id",
            element: <ProffesionalProfile />,
          },
          {
            path: "/my-profile/:id/edit",
            element: <EditProfilePage />,
            action: editProfileAction,
          },
          {
            path: "jobs",
            element: <ProffesionalsJobsAll />,
          },
          {
            path: "jobs/my-jobs",
            element: <ProffesionalsMyJobs />,
          },
        ],
      },
      {
        loader: checkAuthForCompany,
        children: [
          {
            path: "/company-profile/:id",
            element: <CompanyEditProfile />,
            action: editCompanyProfileAction,
          },
          {
            path: "my-jobs",
            element: <MyJobsPage />,
          },
          {
            path: "create-new-job",
            element: <CreateNewJob />,
          },
          {
            path: "my-jobs/:id/candidates",
            element: <CandidatesPage />,
          },
        ],
      },
    ],
  },
  {
    loader: checkAuthForLogin,
    children: [
      {
        path: "login",
        element: <Login />,
        action: loginAction,
      },
      {
        path: "register/company",
        element: <CompanyRegister />,
        action: companyRegisterAction,
      },
      {
        path: "register/professionals",
        element: <ProffesionalsRegister />,
        action: proffesionalRegisterAction,
      },
      {
        path: "register",
        element: <SelectRole />,
      },
    ],
  },
  {
    path: "logout",
    action: logoutAction,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
    action: forgotPasswordAction,
    loader: checkLoggedInUsers,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
    action: resetPasswordAction,
    loader: checkLoggedInUsers,
  },
  {
    loader: checkAuthForLogin,
    children: [
      {
        path: "professionals-approval",
        element: <SalesEngineer />,
        action: salesEngineerAction,
        loader: checkEmployeeAccess,
      },
      {
        path: "company-approval",
        element: <CompanyApproval />,
        action: companyApprovalAction,
        loader: checkCompanyAccess,
      },
    ],
  },
  {
    path: "/google-auth",
    element: <GoogleCallback />,
  },
  {
    path: "company-under-review",
    element: <CompanyUnderReview />,
    loader: checkCompanyReview,
  },
  {
    path: "chat",
    element: <Chat />,
  },
  {
    path: "chat/:conversation_id",
    element: <Chat />,
  },
  {
    path: "admin",
    errorElement: <ErrorPage />,
    children: [
      {
        path: "login",
        element: <AdminLogin />,
        action: adminAction,
        loader: checkAdminIsLogged,
      },
      {
        path: "forgot-password-admin",
        element: <ForgotPasswordAdmin />,
        loader: checkAdminIsLogged,
      },
      {
        path: "reset-password-admin",
        element: <ResetPasswordAdmin />,
        loader: checkAdminIsLogged,
        action: resetPasswordAdminAction,
      },
      {
        path: "logout",
        action: logoutActionAdmin,
      },
      {
        element: <DashRootLayout />,
        loader: checkAdminAuth,
        children: [
          {
            path: "dashboard",
            element: <Dashboard />,
          },
          {
            path: "jobs",
            element: <DashboardJobs />,
          },
          {
            path: "jobs/company-new-job",
            element: <JobFormDash />,
            action: createJobAction,
          },
          {
            path: "jobs/edit-company-job/:id",
            element: <JobFormDash />,
            action: createJobAction,
          },
          {
            path: "jobs/select-candidates/:id",
            element: <SelectCandidates />,
          },
          {
            path: "admins",
            element: <AdminsPage />,
          },
          {
            path: "candidates-profile/:id/edit",
            element: <EditProfilePage />,
            action: editProfileAction,
          },
          {
            path: "company-profile/:id/edit",
            element: <CompanyEditProfile />,
            action: editCompanyProfileAction,
          },
          {
            path: "candidates",
            element: <ProfesionalsPage />,
          },
          {
            path: "companies",
            element: <CompaniesPage />,
          },
        ],
      },
    ],
  },
]);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <div className="fl-page">
        <RouterProvider router={routes} />
      </div>
    </ThemeProvider>
  );
}

export default App;
